// src/utils/formatDate.js
import { format, parse, differenceInYears, differenceInMonths, differenceInDays, subMonths, subYears } from 'date-fns'

/**
 * Formats an ISO date string into a readable format.
 * Automatically converts from UTC to the user's local time zone.
 * @param {string | null | undefined} dateString - The ISO 8601 date string.
 * @param {string} formatStyle - The desired output format (e.g., 'dd MMM yyyy').
 * @returns {string} The formatted date string, or an empty string if the input is invalid.
 */
export const formatReadableDate = (dateString, formatStyle = 'EEE, MMM d, y h:mm a') => {
  if (!dateString) {
    return '-'; // Or return an empty string, based on your preference
  }

  try {
    const date = new Date(dateString);
    return format(date, formatStyle);
  } catch (error) {
    console.error("Invalid date string provided:", dateString);
    return 'Invalid Date';
  }
};


/**
 * Formats "HH:mm" or "HH:mm:ss" to "hh:mm AM/PM".
 */
export const formatReadableTime = (timeString) => {
  if (!timeString) return "-"

  try {
    const parsed = parse(timeString, "HH:mm", new Date())
    return format(parsed, "hh:mm a") // Example: 10:00 → 10:00 AM
  } catch {
    return "-"
  }
}

/**
 * Calculates how long ago the given date was, returning years, months, and days.
 * Example: "2 years, 3 months, 5 days ago"
 * @param {string | Date} dateInput - The input date (string or Date object)
 * @returns {string} The formatted duration since that date.
 */
export const formatSinceDate = (dateInput, nowInput = new Date()) => {
  if (!dateInput) return '-'

  try {
    const start = new Date(dateInput)
    const now = nowInput

    let years = differenceInYears(now, start)
    let tempDate = subYears(now, years)

    let months = differenceInMonths(tempDate, start)
    tempDate = subMonths(tempDate, months)

    let days = differenceInDays(tempDate, start)

    const parts = []
    if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`)
    if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`)
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`)

    return parts.length > 0 ? parts.join(', ') : 'Today'
  } catch (error) {
    console.error('Error calculating time since date:', dateInput, error)
    return '-'
  }
}